import { LANDSCAPE_LOGO_DATA } from './logos.generated.js';
/**
 * Technology logos for the systems landscape diagrams, keyed by `TechStackSystem.logoKey`.
 *
 * Each value is an image data URI (vendor logos, scripts and external links stripped; sources and usage notes in
 * `logos/SOURCES.md`; regenerate with `node scripts/build-landscape-logos.mjs`). A key with no entry falls back to an initials badge, so the diagram never
 * depends on a logo being present.
 */
export const LANDSCAPE_LOGOS = LANDSCAPE_LOGO_DATA;
/** Logo data URI for a key, or null. */
export function landscapeLogo(key) {
    return (key && LANDSCAPE_LOGOS[key]) || null;
}
/**
 * Two-letter badge text for a product name: the capitals of a CamelCase word ("NetSuite" -> "NS",
 * "HubSpot" -> "HS"), else the first letters of the first two words ("Sage Intacct" -> "SI"),
 * else the first two letters.
 */
export function landscapeInitials(name) {
    const words = name
        .replace(/\(.*?\)/g, ' ')
        .split(/[\s/]+/)
        .map((w) => w.replace(/[^A-Za-z0-9.]/g, ''))
        .filter(Boolean);
    if (words.length === 0)
        return '?';
    const caps = (words[0].match(/[A-Z]/g) ?? []).join('');
    if (words.length === 1 && caps.length >= 2)
        return caps.slice(0, 2);
    if (words.length >= 2)
        return (words[0][0] + words[1][0]).toUpperCase();
    return words[0].slice(0, 2).toUpperCase();
}
// Checked in order against the lower-cased name with punctuation turned into spaces.
const LOGO_RULES = [
    [/\bintuit enterprise\b/, 'ies'],
    [/\bquick ?books\b/, 'qbo'],
    [/\bintacct\b/, 'sage_intacct'],
    [/\b(avalara|avatax)\b/, 'avalara'],
    [/\bauthorize net\b/, 'authorize_net'],
    [/\btax ?jar\b/, 'taxjar'],
    // NetSuite's own modules (SuiteBilling, SuiteTax, OpenAir) carry the NetSuite mark.
    [/\b(net ?suite|suite ?(billing|commerce|tax|projects)|open ?air)\b/, 'netsuite'],
    [/\bhub ?spot\b/, 'hubspot'],
    [/\bpay ?pal\b/, 'paypal'],
    // SAP Concur's own icon is the SAP mark.
    [/\b(sap|concur)\b/, 'sap'],
    [/\bexcel\b/, 'excel'],
    [/\bdynamics\b.*\b(crm|sales)\b/, 'dynamics_crm'],
    [/\bbusiness central\b/, 'business_central'],
    [/\bdynamics\b.*\b(finance|f ?o|ax)\b/, 'dynamics_finance'],
    [/\bdynamics\b/, 'dynamics365'],
    [/\badaptive (planning|insights)\b/, 'workday'],
    [/\badobe\b|\badobe ?sign\b/, 'adobe'],
    [/\bzoho ?sign\b/, 'zoho'],
    [/\bj ?p ?morgan\b/, 'jp_morgan'],
    [/\bchase\b/, 'chase'],
    [/\bbank of america\b/, 'bank_of_america'],
    [/\bwells fargo\b/, 'wells_fargo'],
    [/\b(svb|silicon valley bank)\b/, 'svb'],
    [/\bredshift\b/, 'amazon_redshift'],
    [/\bbig ?query\b/, 'google_bigquery'],
    [/\bair ?table\b/, 'airtable'],
    [/\bbig ?commerce\b/, 'bigcommerce'],
    [/\bbig ?time\b/, 'bigtime'],
    [/\bblack ?line\b/, 'blackline'],
    [/\bblue ?snap\b/, 'bluesnap'],
    [/\bavid ?xchange\b/, 'avidxchange'],
    [/\bcyber ?source\b/, 'cybersource'],
    [/\bdeal ?hub\b/, 'dealhub'],
    [/\bdocu ?sign\b/, 'docusign'],
    [/\bpanda ?doc\b/, 'pandadoc'],
    [/\bone ?stream\b/, 'onestream'],
    [/\bnet ?gain\b/, 'netgain'],
    [/\b(kantata|mavenlink)\b/, 'kantata'],
    [/\bg2\b/, 'g2'],
    [/\bclose (crm|io|com)\b|^close$/, 'close'],
    [/\bzone (approvals|billing|capture|payroll|reporting|co)\b|\bby zone\b/, 'zone'],
    // Common words, so only when they are the whole name.
    [/^(linear|cube|tray|zip)( app| software| io| ai| hq)?$/, '$1'],
    [/\bac?cumatica\b/, 'acumatica'],
    [/\b(bill com|divvy)\b|^bill$/, 'bill_com'],
    [/\bgoogle sheets?\b/, 'google_sheets'],
    [/\bpower ?bi\b/, 'power_bi'],
    [/\btri ?net\b/, 'trinet'],
    [/\bfresh ?books\b/, 'freshbooks'],
    [/\bsequoia\b/, 'sequoia_one'],
    [
        /\b(salesforce|pipedrive|xero|stripe|chargebee|zuora|recurly|maxio|anrok|braintree|adyen|rillet)\b/,
        '$1',
    ],
    [
        /\b(adp|brex|campfire|deel|expensify|float|gusto|infor|insperity|justworks|looker|metabase|navan|odoo|paychex|paylocity|ramp|rippling|shopify|tableau|tipalti|ukg|vertex|workday|zoho)\b/,
        '$1',
    ],
    [
        /\b(abacus|airbase|anaplan|asana|atlar|boomi|celigo|conga|connectwise|corpay|coupa|databricks|datarails|drivetrain|ebizcharge|floqast|informatica|ironclad|jitterbit|kintsugi|lawvu|m3ter|merchante|monday|mulesoft|paycom|paystand|pigment|planful|routable|snowflake|sovos|stampli|vena|vendr|versapay|workato)\b/,
        '$1',
    ],
    // Last, so "Oracle NetSuite" and "Sage Intacct" keep their product logos.
    [/\boracle\b/, 'oracle'],
    [/\bsage\b/, 'sage'],
];
/**
 * Logo key for a free-text system name ("QuickBooks Online" -> 'qbo', "NetSuite OneWorld" ->
 * 'netsuite'), or undefined. Used by every diagram that only has a name to go on.
 */
export function landscapeLogoKeyForName(name) {
    const normalized = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    for (const [pattern, key] of LOGO_RULES) {
        const match = normalized.match(pattern);
        if (match)
            return key === '$1' ? match[1] : key;
    }
    return undefined;
}
//# sourceMappingURL=logos.js.map